// app/javascript/controllers/survey_import_status_controller.js
import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";

/**
 * Stimulus controller for displaying real-time survey import progress on clients index.
 * Isolated to individual import items - each import has its own controller instance.
 * Only receives updates for imports belonging to the current admin.
 */
export default class extends Controller {
  static targets = [
    "progressBar",
    "progressText",
    "processedRows",
    "totalRows",
    "statusMessage"
  ];

  static values = {
    importId: Number,
    surveyId: Number
  };

  /**
   * Called when the controller is connected to the DOM.
   */
  connect() {
    console.log("SurveyImportStatus controller connected for import", this.importIdValue);
    this.consumer = createConsumer();
    this.subscribeToChannel();
  }

  /**
   * Called when the controller is disconnected from the DOM.
   */
  disconnect() {
    console.log("SurveyImportStatus controller disconnecting");
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.consumer) {
      this.consumer.disconnect();
    }
  }

  /**
   * Subscribe to SurveyImportChannel for this admin's imports only.
   */
  subscribeToChannel() {
    this.subscription = this.consumer.subscriptions.create(
      "SurveyImportChannel",
      {
        received: (data) => this.handleUpdate(data),
        connected: () => console.log("✓ Connected to SurveyImportChannel"),
        disconnected: () => console.log("✗ Disconnected from SurveyImportChannel")
      }
    );
  }

  /**
   * Handle incoming updates - only process updates for this specific import.
   */
  handleUpdate(data) {
    // Only process updates for this import
    if (data.import_id !== this.importIdValue) {
      return;
    }

    console.log(`Import ${this.importIdValue} update:`, data);

    switch (data.status) {
      case "processing":
        this.updateProgress(data);
        break;
      case "completed":
        this.showCompleted(data);
        break;
      case "failed":
        this.showFailed(data);
        break;
    }
  }

  /**
   * Update progress bar and row counts.
   */
  updateProgress(data) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress}%`;
      this.progressBarTarget.setAttribute("aria-valuenow", data.progress);
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${data.progress}%`;
    }

    if (this.hasProcessedRowsTarget && data.processed !== undefined) {
      this.processedRowsTarget.textContent = data.processed;
    }

    if (this.hasTotalRowsTarget && data.total !== undefined) {
      this.totalRowsTarget.textContent = data.total;
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = `Processing... ${data.processed || 0}/${data.total || 0} rows`;
    }
  }

  /**
   * Show completion and reload page to show new survey.
   */
  showCompleted(data) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "100%";
      this.progressBarTarget.classList.remove("progress-bar-striped", "progress-bar-animated");
      this.progressBarTarget.classList.add("bg-success");
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = "100%";
    }

    if (this.hasStatusMessageTarget) {
      const stats = data.stats || {};
      this.statusMessageTarget.textContent =
        `Complete! Imported ${stats.questions_created || 0} questions`;
    }

    // Reload page after 2 seconds to show the new survey
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  /**
   * Show error state with detailed message.
   */
  showFailed(data) {
    const container = this.element.closest(".alert");
    if (container) {
      container.classList.remove("alert-info");
      container.classList.add("alert-danger");
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove("progress-bar-striped", "progress-bar-animated");
      this.progressBarTarget.classList.add("bg-danger");
    }

    // Update the title to show "Import Failed"
    const titleElement = this.element.querySelector("strong");
    if (titleElement) {
      // Keep the filename but change prefix to "Import Failed"
      const currentText = titleElement.textContent;
      if (currentText.includes("Importing:")) {
        titleElement.textContent = currentText.replace("Importing:", "Import Failed:");
      }
    }

    if (this.hasStatusMessageTarget) {
      // Show detailed error message
      const errorMsg = data.error || "Import failed";
      this.statusMessageTarget.textContent = errorMsg;
      this.statusMessageTarget.classList.remove("text-muted");
      this.statusMessageTarget.classList.add("text-danger");
    }

    // Remove spinner and replace with error icon
    const spinner = this.element.querySelector(".spinner-border");
    if (spinner) {
      const parent = spinner.parentElement;
      spinner.remove();
      // Add error icon if not present
      if (parent && !parent.querySelector(".fa-exclamation-triangle")) {
        const errorIcon = document.createElement("i");
        errorIcon.className = "fas fa-exclamation-triangle mr-3 mt-1 text-danger";
        parent.insertBefore(errorIcon, parent.firstChild);
      }
    }

    // Remove cancel button if present (can't cancel a failed import)
    const cancelButton = this.element.querySelector("[data-action*='cancelImport']");
    if (cancelButton) {
      cancelButton.remove();
    }

    // Add dismiss button if not already present
    const actionsContainer = this.element.querySelector(".ml-3");
    if (actionsContainer && !actionsContainer.querySelector("[data-action*='dismissError']")) {
      const dismissButton = document.createElement("button");
      dismissButton.type = "button";
      dismissButton.className = "btn btn-sm btn-outline-secondary";
      dismissButton.setAttribute("data-action", "click->survey-import-status#dismissError");
      dismissButton.setAttribute("title", "Dismiss notification");
      dismissButton.innerHTML = '<i class="fas fa-times"></i> Dismiss';
      actionsContainer.appendChild(dismissButton);
    }

    // DO NOT auto-dismiss errors - user must manually dismiss
    // Failed imports should persist until the user acknowledges them
  }

  /**
   * Cancel an in-progress import.
   * If the import has already failed, it will be dismissed instead.
   */
  async cancelImport(event) {
    event.preventDefault();

    if (!confirm("Are you sure you want to cancel this import? The empty survey will be deleted.")) {
      return;
    }

    try {
      const response = await fetch(`/surveys/${this.surveyIdValue}/cancel_import`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();

        // If import was already failed, show a brief message before dismissing
        if (data.already_failed) {
          console.log("Import had already failed, dismissing instead");
        }

        // Remove the import card with fade animation
        this.element.style.transition = "opacity 0.3s";
        this.element.style.opacity = "0";
        setTimeout(() => {
          this.element.remove();
        }, 300);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to cancel import:", errorData);
        alert("Failed to cancel import. Please try again.");
      }
    } catch (error) {
      console.error("Error canceling import:", error);
      alert("Failed to cancel import. Please try again.");
    }
  }

  /**
   * Dismiss error message and remove the card.
   * Uses import ID directly, works even if survey was deleted.
   */
  async dismissError(event) {
    if (event) {
      event.preventDefault();
    }

    try {
      // Use import ID directly - works even after survey is deleted
      const response = await fetch(`/survey_imports/${this.importIdValue}/dismiss`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        // Fade out and remove the element
        this.element.style.transition = "opacity 0.3s";
        this.element.style.opacity = "0";
        setTimeout(() => {
          this.element.remove();

          // If no more imports visible, reload to show the survey
          const activeImportsContainer = document.querySelector('[id^="active_imports"]');
          if (activeImportsContainer && activeImportsContainer.querySelectorAll('[id^="import_"]').length === 0) {
            window.location.reload();
          }
        }, 300);
      } else {
        console.error("Failed to dismiss import");
        // Still remove from UI even if backend call fails
        this.element.style.transition = "opacity 0.3s";
        this.element.style.opacity = "0";
        setTimeout(() => {
          this.element.remove();
        }, 300);
      }
    } catch (error) {
      console.error("Error dismissing import:", error);
      // Still remove from UI even if backend call fails
      this.element.style.transition = "opacity 0.3s";
      this.element.style.opacity = "0";
      setTimeout(() => {
        this.element.remove();
      }, 300);
    }
  }
}
